import { Map, X } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";

import {
  ComposableMap,
  Geographies,
  Geography,
} from "@vnedyalk0v/react19-simple-maps";

// Provides the geographic shapes used by the world map.
const GEO_URL = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

// Normalizes country names returned by the API.

const normalizeCountryName = (country) => {
  const normalizedCountry = String(country ?? "")
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();

  // Matches the API name with the country name used by the world map.
  if (normalizedCountry === "United States") {
    return "United States of America";
  }

  // Maps Hong Kong to China for geographic display.
  if (normalizedCountry === "Hong Kong") {
    return "China";
  }

  // Maps Åland Islands to Finland for geographic display.
  if (normalizedCountry === "Åland Islands") {
    return "Finland";
  }

  return normalizedCountry;
};

//Displays the shop locations button and popup.

const ShopLocationsMap = ({ shopList = [] }) => {
  // Controls the shop locations popup.
  const [isOpen, setIsOpen] = useState(false);

  // Stores the selected creation year.
  const [selectedYear, setSelectedYear] = useState("");

  // Stores the country currently under the mouse pointer.
  const [hoveredCountry, setHoveredCountry] = useState(null);

  // Stores the country highlighted from the summary list.
  const [highlightedCountry, setHighlightedCountry] = useState(null);

  // Stores the country currently expanded in the summary list.
  const [expandedCountry, setExpandedCountry] = useState(null);

  // References the map container for tooltip positioning.
  const mapContainerRef = useRef(null);

  // Creates the list of available creation years.
  const availableYears = useMemo(() => {
    const years = shopList
      .map((shop) => {
        const createdAt = new Date(shop?.date_created);

        if (Number.isNaN(createdAt.getTime())) {
          return null;
        }

        return createdAt.getFullYear();
      })
      .filter(Boolean);

    return [...new Set(years)].sort((a, b) => b - a);
  }, [shopList]);

  // Filters shops by the selected creation year.
  const filteredShops = useMemo(() => {
    if (!selectedYear) {
      return shopList;
    }

    return shopList.filter((shop) => {
      const createdAt = new Date(shop?.date_created);

      if (Number.isNaN(createdAt.getTime())) {
        return false;
      }

      return createdAt.getFullYear() === Number(selectedYear);
    });
  }, [shopList, selectedYear]);

  // Groups filtered shops by country.
  const shopsByRegion = useMemo(() => {
    return filteredShops.reduce((regions, shop) => {
      const region = normalizeCountryName(shop?.country);

      if (!region) {
        return regions;
      }

      regions[region] = (regions[region] || 0) + 1;

      return regions;
    }, {});
  }, [filteredShops]);

  // Groups shop locations by normalized country.
  const locationsByRegion = useMemo(() => {
    return filteredShops.reduce((regions, shop) => {
      const region = normalizeCountryName(shop?.country);

      if (!region) {
        return regions;
      }
      // State, brand and Store
      const location = {
        location:
          String(shop?.city ?? "").trim() ||
          String(shop?.state ?? "").trim() ||
          "No city/state",
        brand: String(shop?.brand ?? "").trim(),
        shop: String(shop?.name ?? "").trim(),
      };

      if (!regions[region]) {
        regions[region] = [];
      }

      const alreadyExists = regions[region].some(
        (item) =>
          item.location === location.location &&
          item.brand === location.brand &&
          item.shop === location.shop,
      );

      if (!alreadyExists) {
        regions[region].push(location);
      }

      return regions;
    }, {});
  }, [filteredShops]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 rounded-lg border border-stone-300 bg-stone-50 px-4 py-2 text-sm font-medium text-stone-700 shadow-sm transition hover:bg-stone-100"
      >
        <Map size={18} />
        Average by Period
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Popup background */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsOpen(false)}
          />

          {/* Popup content */}
          <div className="relative w-full max-w-6xl overflow-hidden rounded-2xl border border-stone-200 bg-stone-50 shadow-xl">
            {/* Popup header */}
            <header className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <Map size={20} className="text-stone-700" />

                  <h2 className="text-lg font-semibold text-stone-900">
                    Average by Period
                  </h2>
                </div>

                <p className="mt-1 text-sm text-stone-500">
                  Geographic distribution of registered shops
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close shop locations"
                className="rounded-lg p-2 text-stone-500 transition hover:bg-stone-200 hover:text-stone-900"
              >
                <X size={20} />
              </button>
            </header>

            {/* Popup body */}
            <div className="grid min-h-[520px] grid-cols-1 gap-4 p-4 lg:grid-cols-[2fr_1fr]">
              {/* Map panel */}
              <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                {/* first block */}
                <div className="mb-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-stone-800">
                        Shop Distribution
                      </h3>

                      <p className="mt-1 text-xs text-stone-500">
                        Shops grouped by geographic location
                      </p>
                    </div>

                    {/* Filters the map by creation year */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedYear("")}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                          !selectedYear
                            ? "border-stone-700 bg-stone-800 text-white"
                            : "border-stone-200 bg-white text-stone-600 hover:bg-stone-100"
                        }`}
                      >
                        All
                      </button>

                      {availableYears.map((year) => (
                        <button
                          key={year}
                          type="button"
                          onClick={() => setSelectedYear(String(year))}
                          className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                            selectedYear === String(year)
                              ? "border-stone-700 bg-stone-800 text-white"
                              : "border-stone-200 bg-white text-stone-600 hover:bg-stone-100"
                          }`}
                        >
                          {year}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div
                  ref={mapContainerRef}
                  className="relative flex h-[420px] items-center justify-center overflow-hidden rounded-lg border border-stone-100 bg-stone-50"
                >
                  <ComposableMap
                    projectionConfig={{
                      scale: 125,
                    }}
                    width={800}
                    height={420}
                    className="h-full w-full scale-x-125"
                  >
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) => {
                        // Finds API countries that do not match the map country names.
                        const mapCountryNames = new Set(
                          geographies.map((geo) =>
                            normalizeCountryName(geo?.properties?.name),
                          ),
                        );

                        return geographies.map((geo) => {
                          // Gets the country name from the map data.
                          const countryName = normalizeCountryName(
                            geo?.properties?.name,
                          );

                          // Gets the number of shops registered in this country.
                          const shopCount = shopsByRegion[countryName] || 0;

                          // Highlights the country selected from the summary list.
                          const countryFill =
                            highlightedCountry === countryName
                              ? "#39a"
                              : shopCount > 0
                                ? "#78716c"
                                : "#e7e5e4";

                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={countryFill}
                              stroke="#ffffff"
                              strokeWidth={0.5}
                              tabIndex={-1}
                              onMouseMove={(event) => {
                                if (shopCount === 0) {
                                  setHoveredCountry(null);
                                  return;
                                }

                                const containerBounds =
                                  mapContainerRef.current?.getBoundingClientRect();

                                if (!containerBounds) {
                                  return;
                                }

                                // Updates the tooltip position next to the mouse pointer.
                                setHoveredCountry({
                                  name: countryName,
                                  count: shopCount,
                                  x: event.clientX - containerBounds.left,
                                  y: event.clientY - containerBounds.top,
                                });
                              }}
                              onMouseLeave={() => setHoveredCountry(null)}
                              style={{
                                default: {
                                  outline: "none",
                                },
                                hover: {
                                  fill: shopCount > 0 ? "#39a" : "#e7e5e4",
                                  outline: "none",
                                  cursor: shopCount > 0 ? "pointer" : "default",
                                },
                                pressed: {
                                  fill: countryFill,
                                  outline: "none",
                                },
                              }}
                            />
                          );
                        });
                      }}
                    </Geographies>
                  </ComposableMap>
                  {/* Displays country information next to the mouse pointer. */}
                  {hoveredCountry && (
                    <div
                      className="pointer-events-none absolute z-10 rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700 shadow-md"
                      style={{
                        left: hoveredCountry.x + 12,
                        top: hoveredCountry.y + 12,
                      }}
                    >
                      <p className="font-semibold">{hoveredCountry.name}</p>

                      <p className="mt-0.5 text-stone-500">
                        {hoveredCountry.count}{" "}
                        {hoveredCountry.count === 1 ? "shop" : "shops"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary panel */}
              <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                <div className="border-b border-stone-100 pb-3">
                  <h3 className="text-sm font-semibold text-stone-800">
                    Location Summary
                  </h3>

                  <p className="mt-1 text-xs text-stone-500">
                    Registered shops by location
                  </p>
                </div>

                {/* Displays the number of shops grouped by country. */}
                <div className="mt-4 max-h-[400px] space-y-2 overflow-y-auto pr-1">
                  {Object.entries(shopsByRegion)
                    .sort(
                      ([, firstCount], [, secondCount]) =>
                        secondCount - firstCount,
                    )
                    .map(([country, count]) => (
                      <React.Fragment key={country}>
                        <div
                          onMouseEnter={() => setHighlightedCountry(country)}
                          onMouseLeave={() => setHighlightedCountry(null)}
                          onClick={() =>
                            setExpandedCountry((currentCountry) =>
                              currentCountry === country ? null : country,
                            )
                          }
                          className="flex cursor-pointer items-center justify-between rounded-lg border border-stone-100 bg-stone-50 px-3 py-2 transition hover:bg-stone-100"
                        >
                          <span className="text-sm text-stone-700 transition group-hover:text-stone-900">
                            {country}
                          </span>

                          <span className="text-sm font-semibold text-stone-800">
                            {count}
                          </span>
                        </div>

                        {expandedCountry === country && (
                          <div className="ml-3 rounded-lg border border-stone-100 bg-white px-3 py-2">
                            {locationsByRegion[country]?.map((location) => (
                              <div
                                key={`${location.location}-${location.brand}-${location.shop}`}
                                className="whitespace-nowrap py-1 text-xs text-stone-600"
                              >
                                {location.brand} - {location.shop} - {" "}
                                {location.location}
                              </div>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                </div>
              </div>
            </div>

            {/* Popup footer */}
            <footer className="flex justify-end border-t border-stone-200 bg-white px-6 py-4"></footer>
          </div>
        </div>
      )}
    </>
  );
};

export default ShopLocationsMap;
